import { useState } from "react";
import { toast } from "sonner";

import { autoCompleteFor, inputModeFor } from "@/lib/form-a11y";
import { supabase } from "@/integrations/supabase/client";

const inputClass =
  "mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-base text-foreground outline-none focus:ring-2 focus:ring-accent";

const COUNTRIES = [
  "United States",
  "Canada",
  "Mexico",
  "Honduras",
  "El Salvador",
  "Guatemala",
  "Nicaragua",
  "Costa Rica",
  "Panama",
  "Belize",
  "Brazil",
  "Bolivia",
  "Peru",
  "Other",
];

/**
 * "Grab a Copy!" order request form used by both book pages. Card details are
 * never collected here — the ministry follows up to arrange payment.
 */
export function BookOrderForm({ bookSlug, price }: { bookSlug: string; price: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (key: string) => String(data.get(key) ?? "").trim();

    setSubmitting(true);
    const { error } = await supabase.from("book_orders").insert({
      book_slug: bookSlug,
      first_name: value("first_name"),
      last_name: value("last_name"),
      email: value("email"),
      address1: value("address1") || null,
      address2: value("address2") || null,
      city: value("city") || null,
      state: value("state") || null,
      postal_code: value("postal_code") || null,
      country: value("country") || null,
      quantity: Number(data.get("quantity") ?? 1),
      notes: value("notes") || null,
    });
    setSubmitting(false);

    if (error) {
      toast.error("We couldn't send your request. Please try again.");
      setStatus("We couldn't send your request. Please try again.");
      return;
    }
    toast.success("Thank you! We'll be in touch about your copy.");
    setStatus("Thank you! We'll be in touch about your copy.");
    form.reset();
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-3xl">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="first_name" className="font-sans font-semibold text-foreground">
            First Name <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="first_name"
            name="first_name"
            required
            className={inputClass}
            autoComplete={autoCompleteFor("first_name")}
            inputMode={inputModeFor("first_name")}
          />
        </div>
        <div>
          <label htmlFor="last_name" className="font-sans font-semibold text-foreground">
            Last Name <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="last_name"
            name="last_name"
            required
            className={inputClass}
            autoComplete={autoCompleteFor("last_name")}
            inputMode={inputModeFor("last_name")}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="email" className="font-sans font-semibold text-foreground">
            Email <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className={inputClass}
            autoComplete={autoCompleteFor("email")}
            inputMode={inputModeFor("email")}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="address1" className="font-sans font-semibold text-foreground">
            Address
          </label>
          <input
            id="address1"
            name="address1"
            className={inputClass}
            autoComplete={autoCompleteFor("address1")}
            inputMode={inputModeFor("address1")}
          />
          <input
            id="address2"
            name="address2"
            aria-label="Address line 2"
            className={`${inputClass} mt-3`}
            autoComplete={autoCompleteFor("address2")}
            inputMode={inputModeFor("address2")}
          />
          <p className="mt-1 font-sans text-sm text-muted-foreground">Address 2</p>
        </div>
        <div>
          <label htmlFor="city" className="font-sans font-semibold text-foreground">
            City
          </label>
          <input
            id="city"
            name="city"
            className={inputClass}
            autoComplete={autoCompleteFor("city")}
            inputMode={inputModeFor("city")}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="state" className="font-sans font-semibold text-foreground">
              State
            </label>
            <input
              id="state"
              name="state"
              className={inputClass}
              autoComplete={autoCompleteFor("state")}
              inputMode={inputModeFor("state")}
            />
          </div>
          <div>
            <label htmlFor="postal_code" className="font-sans font-semibold text-foreground">
              Zip/Postal
            </label>
            <input
              id="postal_code"
              name="postal_code"
              className={inputClass}
              autoComplete={autoCompleteFor("postal_code")}
              inputMode={inputModeFor("postal_code")}
            />
          </div>
        </div>
        <div>
          <label htmlFor="country" className="font-sans font-semibold text-foreground">
            Country
          </label>
          <select id="country" name="country" defaultValue="United States" className={inputClass}>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="quantity" className="font-sans font-semibold text-foreground">
            Quantity <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="quantity"
            name="quantity"
            type="number"
            min={1}
            max={100}
            defaultValue={1}
            required
            className={inputClass}
            autoComplete={autoCompleteFor("quantity")}
            inputMode={inputModeFor("quantity")}
          />
          <p className="mt-2 font-sans text-base text-muted-foreground">{price} per copy</p>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="notes" className="font-sans font-semibold text-foreground">
            Notes
          </label>
          <textarea id="notes" name="notes" rows={3} className={inputClass} />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-8 rounded-md bg-accent px-8 py-3 font-sans font-semibold text-navy transition hover:brightness-110 disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Submit"}
      </button>
      <p className="mt-4 font-sans text-sm text-muted-foreground">
        We'll email you to confirm shipping and payment — no card details are collected here.
      </p>
      <p role="status" aria-live="polite" className="sr-only">
        {status}
      </p>
    </form>
  );
}
